import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import type { AxiosError } from "axios";
import { staffWorkdayService } from "../services/staff-workday.service";
import type { CreateStaffWorkdayRequest, ErrorResponse, UpdateStaffWorkdayRequest } from "../types";

export const staffWorkdayKeys = {
	all: ["staff-workday"] as const,
	lists: () => [...staffWorkdayKeys.all, "list"] as const,
	list: () => [...staffWorkdayKeys.lists()] as const,
	details: () => [...staffWorkdayKeys.all, "detail"] as const,
	detail: (id: string) => [...staffWorkdayKeys.details(), id] as const,
};

export function useStaffWorkdays() {
	return useQuery({
		queryKey: staffWorkdayKeys.list(),
		queryFn: () => staffWorkdayService.list(),
	});
}

export function useStaffWorkday(id: string) {
	return useQuery({
		queryKey: staffWorkdayKeys.detail(id),
		queryFn: () => staffWorkdayService.getById(id),
		enabled: !!id,
	});
}

export function useCreateStaffWorkday() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateStaffWorkdayRequest) => staffWorkdayService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: staffWorkdayKeys.lists() });
			message.success("Ish kuni muvaffaqiyatli yaratildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Ish kuni yaratishda xatolik";
			message.error(msg);
		},
	});
}

export function useUpdateStaffWorkday() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateStaffWorkdayRequest }) =>
			staffWorkdayService.update(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: staffWorkdayKeys.lists() });
			queryClient.invalidateQueries({
				queryKey: staffWorkdayKeys.detail(variables.id),
			});
			message.success("Ish kuni muvaffaqiyatli yangilandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Ish kuni yangilashda xatolik";
			message.error(msg);
		},
	});
}

export function useDeleteStaffWorkday() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => staffWorkdayService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: staffWorkdayKeys.lists() });
			message.success("Ish kuni muvaffaqiyatli o'chirildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Ish kuni o'chirishda xatolik";
			message.error(msg);
		},
	});
}
